import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, collection, query, where, orderBy, getDocs } from 'firebase/firestore';
import {
  FileText,
  Activity,
  Heart,
  Droplets,
  Scale,
  Share2,
  Download,
  ChevronLeft,
  TrendingUp,
  TrendingDown,
  Clock,
  Calendar,
  AlertTriangle,
  ClipboardList,
  Activity as ActivityIcon,
  User,
  Pill,
  ChevronRight,
  Mail,
  MessageCircle,
  Phone,
  ArrowUpRight,
  ArrowDownRight,
  Thermometer,
  Dumbbell,
  Brain,
  Wind,
  Stethoscope,
  UserPlus,
  Syringe,
} from 'lucide-react';
import useAuthStore from '@store/authStore.js';
import { db } from '@/config/firebase.js';
import { formatDate } from '@/Utils/dateUtils.js';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from 'recharts';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

// Constants
const TIME_RANGES = {
  ALL: 'all',
  MONTHLY: 'monthly',
  WEEKLY: 'weekly',
  DAILY: 'daily',
};

const HEALTH_METRICS = {
  BLOOD_PRESSURE: 'bloodPressure',
  BLOOD_SUGAR: 'bloodSugar',
  HEART_RATE: 'heartRate',
  TEMPERATURE: 'temperature',
  WEIGHT: 'weight',
  OXYGEN: 'oxygenLevel',
  BMI: 'bmi',
  RESPIRATORY_RATE: 'respiratoryRate',
};

// Helper Components
const TabButton = ({ active, onClick, children, icon: Icon }) => (
  <button
    onClick={onClick}
    className={`
            flex items-center px-4 py-2 rounded-lg font-medium transition-all
            ${
              active
                ? 'bg-primary-100 text-primary-700 dark:bg-primary-900 dark:text-primary-100 shadow-sm'
                : 'text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200'
            }
        `}
  >
    {Icon && <Icon className="w-4 h-4 mr-2" />}
    {children}
  </button>
);

const ShareButton = ({ icon: Icon, label, onClick, className }) => (
  <button
    onClick={onClick}
    className={`
            flex items-center px-3 py-2 rounded-lg text-sm font-medium 
            transition-all shadow-sm hover:shadow print:hidden
            ${className}
        `}
  >
    <Icon className="w-4 h-4 mr-2" />
    {label}
  </button>
);

const MetricCard = ({ title, value, unit, icon: Icon, trend, color = 'blue' }) => {
  const bgColor = `bg-${color}-50 dark:bg-${color}-900/20`;
  const textColor = `text-${color}-600 dark:text-${color}-400`;

  return (
    <div className={`p-4 rounded-lg ${bgColor}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          {Icon && <Icon className={`w-5 h-5 mr-2 ${textColor}`} />}
          <span className="text-sm text-gray-600 dark:text-gray-400">{title}</span>
        </div>
        {trend && (
          <div className={`flex items-center ${trend > 0 ? 'text-green-500' : 'text-red-500'}`}>
            {trend > 0 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
            <span className="ml-1 text-xs">{Math.abs(trend)}%</span>
          </div>
        )}
      </div>
      <p className={`text-lg font-semibold mt-1 ${textColor}`}>
        {value}
        {unit && <span className="text-sm ml-1">{unit}</span>}
      </p>
    </div>
  );
};

const MetricChart = ({ data, dataKey, color, title, yAxisLabel, connectNulls = true }) => (
  <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm">
    <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-4">{title}</h3>
    <div className="h-48">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 5, right: 5, left: 5, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" opacity={0.1} />
          <XAxis dataKey="date" fontSize={12} tickMargin={10} />
          <YAxis
            label={{
              value: yAxisLabel,
              angle: -90,
              position: 'insideLeft',
              fontSize: 12,
            }}
            fontSize={12}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: 'rgba(255, 255, 255, 0.95)',
              border: 'none',
              borderRadius: '8px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            }}
          />
          <Line
            type="monotone"
            dataKey={dataKey}
            stroke={color}
            strokeWidth={2}
            dot={{ r: 4 }}
            connectNulls={connectNulls}
            activeDot={{ r: 6, stroke: 'white', strokeWidth: 2 }}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  </div>
);

// Main Component
const PatientReportPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [loading, setLoading] = useState(true);
  const [patient, setPatient] = useState(null);
  const [healthRecords, setHealthRecords] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [activeTimeRange, setActiveTimeRange] = useState(TIME_RANGES.ALL);
  const [error, setError] = useState(null);
  const [exportLoading, setExportLoading] = useState(false);

  // Data Fetching
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch patient data
        const patientDoc = await getDoc(doc(db, 'users', id));
        if (!patientDoc.exists()) {
          throw new Error('Patient not found');
        }

        // Fetch health records with proper date handling
        const healthRecordsRef = collection(db, 'health_records');
        const healthQuery = query(
          healthRecordsRef,
          where('patientId', '==', id),
          orderBy('recordedAt', 'desc')
        );

        const healthSnapshot = await getDocs(healthQuery);
        const records = healthSnapshot.docs.map((doc) => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            recordedAt: data.recordedAt?.seconds
              ? new Date(data.recordedAt.seconds * 1000)
              : data.recordedAt instanceof Date
                ? data.recordedAt
                : new Date(data.recordedAt),
          };
        });

        // Fetch appointments with proper date handling
        const appointmentsRef = collection(db, 'tickets');
        const appointmentsQuery = query(
          appointmentsRef,
          where('patientId', '==', id),
          orderBy('dateTime', 'desc')
        );

        const appointmentsSnapshot = await getDocs(appointmentsQuery);
        const appointments = appointmentsSnapshot.docs.map((doc) => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            dateTime: data.dateTime?.seconds
              ? new Date(data.dateTime.seconds * 1000)
              : data.dateTime instanceof Date
                ? data.dateTime
                : new Date(data.dateTime),
          };
        });

        setPatient({ id: patientDoc.id, ...patientDoc.data() });
        setHealthRecords(records);
        setAppointments(appointments);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  // Filtered Data Based on Time Range
  const filteredHealthRecords = useMemo(() => {
    const now = new Date();
    const records = [...healthRecords];

    switch (activeTimeRange) {
      case TIME_RANGES.DAILY:
        return records.filter((record) => {
          const recordDate = new Date(record.recordedAt);
          return recordDate.toDateString() === now.toDateString();
        });
      case TIME_RANGES.WEEKLY:
        const weekAgo = new Date(now);
        weekAgo.setDate(weekAgo.getDate() - 7);
        return records.filter((record) => new Date(record.recordedAt) >= weekAgo);
      case TIME_RANGES.MONTHLY:
        const monthAgo = new Date(now);
        monthAgo.setMonth(monthAgo.getMonth() - 1);
        return records.filter((record) => new Date(record.recordedAt) >= monthAgo);
      default:
        return records;
    }
  }, [healthRecords, activeTimeRange]);

  // Chart Data Processing
  const chartData = useMemo(() => {
    return filteredHealthRecords
      .map((record) => ({
        date: formatDate(record.recordedAt),
        bloodSugar: record.bloodSugar,
        systolicBP: parseInt(record.bloodPressure?.split('/')[0]),
        diastolicBP: parseInt(record.bloodPressure?.split('/')[1]),
        heartRate: record.heartRate,
        temperature: record.temperature,
        weight: record.weight,
        oxygenLevel: record.oxygenLevel,
        respiratoryRate: record.respiratoryRate,
      }))
      .reverse();
  }, [filteredHealthRecords]);

  // Calculated Metrics
  const calculateHealthMetrics = (records) => {
    if (!records.length) return null;
    const latest = records[0];
    const previous = records[1];

    const calculateTrend = (current, prev) => {
      if (!current || !prev) return null;
      return (((current - prev) / prev) * 100).toFixed(1);
    };

    return {
      bloodPressure: {
        value: latest.bloodPressure,
        trend: previous?.bloodPressure
          ? calculateTrend(
              parseInt(latest.bloodPressure?.split('/')[0]),
              parseInt(previous.bloodPressure?.split('/')[0])
            )
          : null,
      },
      bloodSugar: {
        value: latest.bloodSugar,
        trend: calculateTrend(latest.bloodSugar, previous?.bloodSugar),
      },
      heartRate: {
        value: latest.heartRate,
        trend: calculateTrend(latest.heartRate, previous?.heartRate),
      },
      temperature: {
        value: latest.temperature,
        trend: calculateTrend(latest.temperature, previous?.temperature),
      },
      weight: {
        value: latest.weight,
        trend: calculateTrend(latest.weight, previous?.weight),
      },
      oxygenLevel: {
        value: latest.oxygenLevel,
        trend: calculateTrend(latest.oxygenLevel, previous?.oxygenLevel),
      },
    };
  };

  // Handlers
  const handleShare = async (method) => {
    try {
      const reportText = generateReportText();

      switch (method) {
        case 'email':
          const emailBody = encodeURIComponent(reportText);
          window.location.href = `mailto:${patient.email}?subject=Medical Report&body=${emailBody}`;
          break;
        case 'whatsapp':
          window.open(
            `https://wa.me/${patient.mobile}?text=${encodeURIComponent(reportText)}`,
            '_blank'
          );
          break;
        case 'sms':
          window.location.href = `sms:${patient.mobile}?body=${encodeURIComponent(reportText)}`;
          break;
        default:
          throw new Error('Unsupported share method');
      }
    } catch (err) {
      console.error('Error sharing report:', err);
    }
  };

  const generateReportText = () => {
    const metrics = calculateHealthMetrics(filteredHealthRecords);
    return `
Medical Report for ${patient.name}
Generated on ${formatDate(new Date())}

Time Period: ${activeTimeRange.toUpperCase()}

Latest Health Metrics:
${
  metrics
    ? `
- Blood Pressure: ${metrics.bloodPressure.value || 'N/A'}
- Blood Sugar: ${metrics.bloodSugar.value || 'N/A'} mg/dL
- Heart Rate: ${metrics.heartRate.value || 'N/A'} bpm
- Temperature: ${metrics.temperature.value || 'N/A'}°C
- Weight: ${metrics.weight.value || 'N/A'} kg
- Oxygen Level: ${metrics.oxygenLevel.value || 'N/A'}%
`
    : 'No recent metrics available'
}

Next Appointment: ${
      appointments.find((a) => new Date(a.dateTime) > new Date())
        ? formatDate(appointments.find((a) => new Date(a.dateTime) > new Date()).dateTime)
        : 'None scheduled'
    }

This is an automated report. Please contact your healthcare provider for any questions.
`;
  };

  const handleExportPDF = async () => {
    try {
      setExportLoading(true);
      const reportElement = document.getElementById('patient-report');

      // Add print class to get print styling
      reportElement.classList.add('printing');

      const canvas = await html2canvas(reportElement, {
        scale: 2,
        useCORS: true,
        logging: false,
        windowWidth: 1200, // Fixed width for consistency
        onclone: (document) => {
          // Apply any specific styles for PDF export
          const element = document.getElementById('patient-report');
          element.style.padding = '20px';
          element.style.backgroundColor = 'white';
        },
      });

      const imgData = canvas.toDataURL('image/png');

      // Create PDF with proper dimensions
      const pdfWidth = 210; // A4 width in mm
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;

      const pdf = new jsPDF({
        orientation: pdfHeight > pdfWidth ? 'portrait' : 'landscape',
        unit: 'mm',
        format: [pdfWidth, pdfHeight],
      });

      pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
      pdf.save(`${patient.name}_medical_report_${activeTimeRange}.pdf`);

      // Remove print class
      reportElement.classList.remove('printing');
    } catch (error) {
      console.error('Error generating PDF:', error);
    } finally {
      setExportLoading(false);
    }
  };

  const calculateHealthRiskLevel = (records) => {
    if (!records.length) return { level: 'Unknown', color: 'gray' };

    const latest = records[0];
    let riskScore = 0;
    let riskFactors = [];

    // Blood Pressure Check
    if (latest.bloodPressure) {
      const systolic = parseInt(latest.bloodPressure.split('/')[0]);
      if (systolic >= 140) {
        riskScore += 2;
        riskFactors.push('High blood pressure');
      } else if (systolic >= 130) {
        riskScore += 1;
        riskFactors.push('Elevated blood pressure');
      }
    }

    // Blood Sugar Check
    if (latest.bloodSugar) {
      if (latest.bloodSugar > 200) {
        riskScore += 2;
        riskFactors.push('High blood sugar');
      } else if (latest.bloodSugar > 140) {
        riskScore += 1;
        riskFactors.push('Elevated blood sugar');
      }
    }

    // Heart Rate Check
    if (latest.heartRate) {
      if (latest.heartRate > 100 || latest.heartRate < 60) {
        riskScore += 1;
        riskFactors.push('Abnormal heart rate');
      }
    }

    // Determine risk level
    if (riskScore === 0) return { level: 'Low', color: 'green', factors: riskFactors };
    if (riskScore <= 2) return { level: 'Moderate', color: 'yellow', factors: riskFactors };
    return { level: 'High', color: 'red', factors: riskFactors };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen">
        <p className="text-red-500 mb-4">{error}</p>
        <button onClick={() => navigate(-1)} className="btn-secondary">
          Go Back
        </button>
      </div>
    );
  }

  const healthRisk = calculateHealthRiskLevel(filteredHealthRecords);
  const metrics = calculateHealthMetrics(filteredHealthRecords);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header Actions - Hidden in Print */}
      <div className="sticky top-0 z-50 bg-white dark:bg-gray-800 border-b dark:border-gray-700 print:hidden">
        <div className="container mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <button onClick={() => navigate(-1)} className="btn-outline flex items-center">
              <ChevronLeft className="w-4 h-4 mr-2" />
              Back to Profile
            </button>

            <div className="flex space-x-2">
              <ShareButton
                icon={Mail}
                label="Email"
                onClick={() => handleShare('email')}
                className="bg-blue-50 text-blue-600 hover:bg-blue-100 dark:bg-blue-900/20"
              />
              <ShareButton
                icon={MessageCircle}
                label="WhatsApp"
                onClick={() => handleShare('whatsapp')}
                className="bg-green-50 text-green-600 hover:bg-green-100 dark:bg-green-900/20"
              />
              <ShareButton
                icon={Phone}
                label="SMS"
                onClick={() => handleShare('sms')}
                className="bg-purple-50 text-purple-600 hover:bg-purple-100 dark:bg-purple-900/20"
              />
              <button
                onClick={handleExportPDF}
                disabled={exportLoading}
                className="btn-primary flex items-center"
              >
                {exportLoading ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                ) : (
                  <Download className="w-4 h-4 mr-2" />
                )}
                Export PDF
              </button>
            </div>
          </div>

          {/* Time Range Tabs */}
          <div className="flex space-x-2 mt-4">
            {Object.entries(TIME_RANGES).map(([key, value]) => (
              <TabButton
                key={value}
                active={activeTimeRange === value}
                onClick={() => setActiveTimeRange(value)}
                icon={
                  value === TIME_RANGES.ALL
                    ? ClipboardList
                    : value === TIME_RANGES.MONTHLY
                      ? Calendar
                      : value === TIME_RANGES.WEEKLY
                        ? Clock
                        : Clock
                }
              >
                {key.charAt(0) + key.slice(1).toLowerCase()}
              </TabButton>
            ))}
          </div>
        </div>
      </div>

      {/* Main Report Content */}
      <div id="patient-report" className="container mx-auto px-4 py-8 space-y-8">
        {/* Report Header */}
        <div className="bg-gradient-to-r from-primary-600 to-primary-700 rounded-xl p-8 text-white">
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-3xl font-bold">dru Patient Health Report</h1>
              <p className="mt-2 text-primary-100">
                {activeTimeRange.charAt(0).toUpperCase() + activeTimeRange.slice(1)} Report •
                Generated on {formatDate(new Date())}
              </p>
            </div>
            <div className="text-right">
              <p className="text-lg font-semibold">{patient.name}</p>
              <p className="text-sm text-primary-100">Patient ID: {patient.id}</p>
            </div>
          </div>
        </div>

        {/* Quick Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div
            className={`p-4 rounded-lg bg-${healthRisk.color}-50 dark:bg-${healthRisk.color}-900/20`}
          >
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-600 dark:text-gray-400">Health Risk Level</p>
              <AlertTriangle className={`w-5 h-5 text-${healthRisk.color}-500`} />
            </div>
            <p className={`text-lg font-bold text-${healthRisk.color}-600`}>{healthRisk.level}</p>
            {healthRisk.factors?.length > 0 && (
              <p className="text-sm text-gray-500 mt-1">{healthRisk.factors[0]}</p>
            )}
          </div>
          <div className="p-4 rounded-lg bg-blue-50 dark:bg-blue-900/20">
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-600 dark:text-gray-400">Records</p>
              <ClipboardList className="w-5 h-5 text-blue-500" />
            </div>
            <p className="text-lg font-bold text-blue-600">{filteredHealthRecords.length}</p>
            <p className="text-sm text-gray-500 mt-1">In selected period</p>
          </div>
          <div className="p-4 rounded-lg bg-green-50 dark:bg-green-900/20">
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-600 dark:text-gray-400">Next Check-up</p>
              <Calendar className="w-5 h-5 text-green-500" />
            </div>
            <p className="text-lg font-bold text-green-600">
              {appointments.find((a) => new Date(a.dateTime) > new Date())
                ? formatDate(appointments.find((a) => new Date(a.dateTime) > new Date()).dateTime)
                : 'None scheduled'}
            </p>
          </div>
          <div className="p-4 rounded-lg bg-purple-50 dark:bg-purple-900/20">
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-600 dark:text-gray-400">Last Updated</p>
              <Clock className="w-5 h-5 text-purple-500" />
            </div>
            <p className="text-lg font-bold text-purple-600">
              {filteredHealthRecords[0] ? formatDate(filteredHealthRecords[0].recordedAt) : 'N/A'}
            </p>
          </div>
        </div>

        {/* Health Metrics Charts */}
        <section className="space-y-6">
          <h2 className="text-xl font-bold flex items-center">
            <Activity className="w-5 h-5 mr-2 text-primary-500" />
            Health Trends
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <MetricChart
              data={chartData}
              dataKey="bloodSugar"
              color="#6366f1"
              title="Blood Sugar Levels"
              yAxisLabel="mg/dL"
            />
            <MetricChart
              data={chartData}
              dataKey="systolicBP"
              color="#ef4444"
              title="Blood Pressure (Systolic)"
              yAxisLabel="mmHg"
            />
            <MetricChart
              data={chartData}
              dataKey="heartRate"
              color="#10b981"
              title="Heart Rate"
              yAxisLabel="bpm"
            />
            <MetricChart
              data={chartData}
              dataKey="oxygenLevel"
              color="#6366f1"
              title="Oxygen Saturation"
              yAxisLabel="%"
            />
          </div>
        </section>

        {/* Current Metrics Overview */}
        <section className="space-y-6">
          <h2 className="text-xl font-bold flex items-center">
            <Stethoscope className="w-5 h-5 mr-2 text-primary-500" />
            Current Health Metrics
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <MetricCard
              title="Blood Pressure"
              value={
                metrics?.bloodPressure.value ||
                healthRecords.find((r) => r.bloodPressure)?.bloodPressure ||
                'N/A'
              }
              unit="mmHg"
              icon={Heart}
              trend={metrics?.bloodPressure.trend}
              color="red"
            />
            <MetricCard
              title="Blood Sugar"
              value={
                metrics?.bloodSugar.value ||
                healthRecords.find((r) => r.bloodSugar)?.bloodSugar ||
                'N/A'
              }
              unit="mg/dL"
              icon={Droplets}
              trend={metrics?.bloodSugar.trend}
              color="blue"
            />
            <MetricCard
              title="Heart Rate"
              value={
                metrics?.heartRate.value ||
                healthRecords.find((r) => r.heartRate)?.heartRate ||
                'N/A'
              }
              unit="bpm"
              icon={Activity}
              trend={metrics?.heartRate.trend}
              color="green"
            />
            <MetricCard
              title="Temperature"
              value={
                metrics?.temperature.value ||
                healthRecords.find((r) => r.temperature)?.temperature ||
                'N/A'
              }
              unit="°C"
              icon={Thermometer}
              trend={metrics?.temperature.trend}
              color="yellow"
            />
            <MetricCard
              title="Weight"
              value={metrics?.weight.value || healthRecords.find((r) => r.weight)?.weight || 'N/A'}
              unit="kg"
              icon={Scale}
              trend={metrics?.weight.trend}
              color="purple"
            />
            <MetricCard
              title="Oxygen Level"
              value={
                metrics?.oxygenLevel.value ||
                healthRecords.find((r) => r.oxygenLevel)?.oxygenLevel ||
                'N/A'
              }
              unit="%"
              icon={Wind}
              trend={metrics?.oxygenLevel.trend}
              color="indigo"
            />
          </div>
        </section>

        {/* Detailed Health Records */}
        <section className="space-y-6">
          <h2 className="text-xl font-bold flex items-center">
            <ClipboardList className="w-5 h-5 mr-2 text-primary-500" />
            Health Records
          </h2>

          <div className="space-y-4">
            {filteredHealthRecords.map((record, index) => (
              <div
                key={record.id}
                className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 hover:shadow-md transition-shadow"
              >
                <div className="flex justify-between">
                  <div className="space-y-2">
                    <div className="flex items-center">
                      <Calendar className="w-4 h-4 text-primary-500 mr-2" />
                      <p className="font-semibold">{formatDate(record.recordedAt)}</p>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {record.bloodPressure && (
                        <div>
                          <p className="text-sm text-gray-500">Blood Pressure</p>
                          <p className="font-medium">{record.bloodPressure} mmHg</p>
                        </div>
                      )}
                      {record.bloodSugar && (
                        <div>
                          <p className="text-sm text-gray-500">Blood Sugar</p>
                          <p className="font-medium">{record.bloodSugar} mg/dL</p>
                        </div>
                      )}
                      {record.heartRate && (
                        <div>
                          <p className="text-sm text-gray-500">Heart Rate</p>
                          <p className="font-medium">{record.heartRate} bpm</p>
                        </div>
                      )}
                      {record.temperature && (
                        <div>
                          <p className="text-sm text-gray-500">Temperature</p>
                          <p className="font-medium">{record.temperature}°C</p>
                        </div>
                      )}
                    </div>
                    {record.notes && (
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                        Notes: {record.notes}
                      </p>
                    )}
                  </div>
                  {index > 0 && (
                    <div className="flex flex-col items-end text-sm">
                      {record.bloodSugar && healthRecords[index - 1]?.bloodSugar && (
                        <div
                          className={`flex items-center ${
                            record.bloodSugar > healthRecords[index - 1].bloodSugar
                              ? 'text-red-500'
                              : 'text-green-500'
                          }`}
                        >
                          {record.bloodSugar > healthRecords[index - 1].bloodSugar ? (
                            <ArrowUpRight className="w-4 h-4 mr-1" />
                          ) : (
                            <ArrowDownRight className="w-4 h-4 mr-1" />
                          )}
                          {Math.abs(
                            record.bloodSugar - healthRecords[index - 1].bloodSugar
                          ).toFixed(1)}{' '}
                          mg/dL
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Medications and Treatments */}
        {patient.medications && (
          <section className="space-y-6">
            <h2 className="text-xl font-bold flex items-center">
              <Pill className="w-5 h-5 mr-2 text-primary-500" />
              Current Medications
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {patient.medications.map((med, index) => (
                <div key={index} className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-semibold text-gray-900 dark:text-white">{med.name}</p>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        Dosage: {med.dosage}
                      </p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Frequency: {med.frequency}
                      </p>
                    </div>
                    <Pill className="w-5 h-5 text-primary-500" />
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Health Insights and Recommendations */}
        <section className="space-y-6">
          <h2 className="text-xl font-bold flex items-center">
            <Brain className="w-5 h-5 mr-2 text-primary-500" />
            Health Insights
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm">
              <h3 className="font-semibold mb-4 flex items-center">
                <AlertTriangle className="w-4 h-4 mr-2 text-yellow-500" />
                Risk Factors
              </h3>
              <div className="space-y-3">
                {healthRisk.factors?.map((factor, index) => (
                  <div key={index} className="flex items-center text-gray-700 dark:text-gray-300">
                    <div className="w-2 h-2 rounded-full bg-red-500 mr-3"></div>
                    <span>{factor}</span>
                  </div>
                ))}
                {(!healthRisk.factors || healthRisk.factors.length === 0) && (
                  <p className="text-gray-500">No significant risk factors identified</p>
                )}
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm">
              <h3 className="font-semibold mb-4 flex items-center">
                <Stethoscope className="w-4 h-4 mr-2 text-green-500" />
                Recommendations
              </h3>
              <div className="space-y-3">
                {healthRisk.factors?.map((factor, index) => (
                  <div key={index} className="flex items-center text-gray-700 dark:text-gray-300">
                    <ChevronRight className="w-4 h-4 mr-2 text-primary-500" />
                    <span>
                      {factor.includes('blood pressure') && 'Monitor blood pressure regularly'}
                      {factor.includes('blood sugar') && 'Regular glucose monitoring advised'}
                      {factor.includes('heart rate') && 'Schedule cardiac evaluation'}
                    </span>
                  </div>
                ))}
                <div className="flex items-center text-gray-700 dark:text-gray-300">
                  <ChevronRight className="w-4 h-4 mr-2 text-primary-500" />
                  <span>
                    Schedule next check-up in {healthRisk.level === 'High' ? '2 weeks' : '1 month'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Report Footer */}
        <footer className="mt-12 pt-6 border-t border-gray-200 dark:border-gray-700">
          <div className="text-sm text-gray-500 dark:text-gray-400">
            <div className="flex justify-between">
              <div>
                <p>Report generated by {user?.name || 'dru Health Care'}</p>
                <p className="mt-1">Last updated: {formatDate(new Date())}</p>
              </div>
            </div>

            <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <p className="text-xs leading-relaxed">
                This medical report contains confidential health information and is protected by
                law. The information provided is based on records from the selected time period (
                {activeTimeRange}). For medical emergencies, please contact us or visit your nearest
                healthcare facility.
              </p>
              <p className="text-xs mt-2">Report ID: {`${patient.id}-${Date.now()}`}</p>
            </div>
          </div>
        </footer>
      </div>

      {/* Print-specific styles */}
      <style jsx global>{`
        @media print {
          body {
            background: white !important;
            color: black !important;
          }

          #patient-report {
            padding: 20px !important;
            max-width: none !important;
          }

          //.print:hidden {
          //    display: none !important;
          //}

          /* Ensure charts are visible */
          .recharts-surface {
            background: white !important;
          }

          /* Improve contrast for text */
          p,
          h1,
          h2,
          h3,
          h4,
          h5,
          h6 {
            color: black !important;
          }

          /* Force background colors to print */
          * {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }

          /* Break pages appropriately */
          section {
            page-break-inside: avoid;
          }

          /* Ensure gradients print */
          .bg-gradient-to-r {
            background: rgb(79, 70, 229) !important;
            color: white !important;
          }

          /* Remove hover effects */
          //.hover\:shadow-md:hover {
          //    box-shadow: none !important;
          //}

          /* Adjust metrics cards for better printing */
          .metric-card {
            break-inside: avoid;
            background: white !important;
            border: 1px solid #e5e7eb !important;
          }

          /* Ensure charts print on their own page */
          .charts-section {
            page-break-before: always;
            page-break-after: always;
          }

          /* Remove unnecessary borders and shadows */
          .shadow-sm,
          .shadow-md,
          .shadow-lg {
            box-shadow: none !important;
          }

          /* Ensure good contrast for light text */
          .text-gray-500,
          .text-gray-400 {
            color: #4b5563 !important;
          }
        }
      `}</style>
    </div>
  );
};

export default PatientReportPage;
